import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, QrCode } from 'lucide-react';
import { Button } from '../../Button';
import { describeError } from '../../../services/errorMessage';
import type { MaterialArtikel } from '../../../services/material/materialTypes';

/**
 * Rückfrage vor „Karte erneuern“ — eigener Dialog statt Browser-Dialog
 * (CI-Ratsche), Aufbau wie `MangelLoeschenDialog`. Der neue Token macht jede
 * gedruckte Karte dieses Artikels ungültig; das lässt sich nicht zurücknehmen.
 * Escape und ein Klick daneben brechen ab, solange nichts läuft.
 */

interface Props {
    artikel: MaterialArtikel;
    onErneuern: () => Promise<unknown>;
    onAbbrechen: () => void;
}

export const KarteErneuernDialog: React.FC<Props> = ({ artikel, onErneuern, onAbbrechen }) => {
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState('');
    const abbrechenRef = useRef<HTMLButtonElement | null>(null);
    const laeuftRef = useRef(false);

    // Fokus auf „Abbrechen“: Enter allein soll keine Karten entwerten.
    useEffect(() => { abbrechenRef.current?.focus(); }, []);

    useEffect(() => {
        const taste = (e: KeyboardEvent) => {
            if (e.key !== 'Escape' || laeuftRef.current) return;
            e.preventDefault();
            e.stopPropagation();
            onAbbrechen();
        };
        document.addEventListener('keydown', taste, true);
        return () => document.removeEventListener('keydown', taste, true);
    }, [onAbbrechen]);

    const erneuern = async () => {
        if (laeuftRef.current) return;
        laeuftRef.current = true;
        setLaeuft(true);
        setFehler('');
        try {
            await onErneuern();
        } catch (e) {
            setFehler(describeError(e));
        } finally {
            laeuftRef.current = false;
            setLaeuft(false);
        }
    };

    const abbrechen = () => { if (!laeuftRef.current) onAbbrechen(); };

    return (
        <div className="fixed inset-0 z-[160] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={abbrechen}>
            <div
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="material-erneuern-titel"
                aria-describedby="material-erneuern-text"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-full overflow-y-auto border border-slate-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="p-6 flex gap-3 items-start">
                    <div className="p-2 rounded-xl shrink-0 bg-amber-100 text-amber-700">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div className="min-w-0 space-y-2">
                        <h3 id="material-erneuern-titel" className="font-bold text-lg text-slate-800">Karte erneuern?</h3>
                        <div id="material-erneuern-text" className="text-sm text-slate-600 leading-relaxed space-y-2">
                            <p className="font-semibold text-slate-800">Alte Karten werden ungültig.</p>
                            <p>
                                „{artikel.name}“ bekommt einen neuen QR-Code. Wer eine alte Karte scannt, sieht „Karte ungültig“ und
                                kann nichts mehr melden. Danach die neue Karte drucken und die alte ersetzen.
                            </p>
                        </div>
                        {fehler && <p role="alert" className="text-xs text-red-700">{fehler}</p>}
                    </div>
                </div>
                <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col-reverse sm:flex-row justify-end gap-2 rounded-b-2xl">
                    <button
                        ref={abbrechenRef}
                        type="button"
                        onClick={abbrechen}
                        disabled={laeuft}
                        className="min-h-[44px] px-4 py-2 rounded-xl text-sm font-semibold text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                    >
                        Abbrechen
                    </button>
                    <Button variant="danger" className="min-h-[44px]" icon={<QrCode className="w-4 h-4" />} loading={laeuft} onClick={() => { void erneuern(); }}>
                        Karte erneuern
                    </Button>
                </div>
            </div>
        </div>
    );
};
